// src/services/accounting.ts

import { IESDoc } from "../app.interface";
import axiosInstance from "../middlewares/axiosInstance";
import {
  IListCoaRequest,
  IListCoaResponse,
  ICreateCoaRequest,
  ICreateCoaResponse,
  IUpdateCoaRequest,
  IUpdateCoaResponse,
  IDeleteCoaRequest,
  IDeleteCoaResponse,
  ISplitCoaRequest,
  ISplitCoaResponse,
  IMergeCoaRequest,
  IMergeCoaResponse,
  ITransferBalanceRequest,
  ITransferBalanceResponse,
  // Journal interfaces
  IListJournalEntriesRequest,
  IListJournalEntriesResponse,
  IDraftJournalEntryRequest,
  IDraftJournalEntryResponse,
  IPostJournalEntryRequest,
  IPostJournalEntryResponse,
  IGetJournalEntryRequest,
  IGetJournalEntryResponse,
  IDeleteJournalEntryRequest,
  IDeleteJournalEntryResponse,
  IPostDraftJournalEntryRequest,
  IPostDraftJournalEntryResponse,
  IReverseJournalEntryRequest,
  IReverseJournalEntryResponse,
  // Ledger interfaces
  ILedgerSummaryRequest,
  ILedgerSummaryResponse,
  ILedgerEntriesRequest,
  ILedgerEntriesResponse,
  IJournalDetailsRequest,
  IJournalDetailsResponse,
  // Voucher interfaces
  IListVouchersRequest,
  IListVouchersResponse,
  IGetVoucherTemplatesRequest,
  IGetVoucherTemplatesResponse,
  ICreateDraftVoucherRequest,
  ICreateDraftVoucherResponse,
  IPostVoucherRequest,
  IPostVoucherResponse,
  IReverseVoucherRequest,
  IReverseVoucherResponse,
  IGetVoucherRequest,
  IGetVoucherResponse,
  IUpdateDraftVoucherRequest,
  IUpdateDraftVoucherResponse,
  IDeleteVoucherRequest,
  IDeleteVoucherResponse,
  // Voucher Template interfaces
  IListVoucherTemplatesRequest,
  IListVoucherTemplatesResponse,
  IGetVoucherTemplateRequest,
  IGetVoucherTemplateResponse,
  ICreateVoucherTemplateRequest,
  ICreateVoucherTemplateResponse,
  IUpdateVoucherTemplateRequest,
  IUpdateVoucherTemplateResponse,
  IToggleTemplateStatusRequest,
  IToggleTemplateStatusResponse,
  IDuplicateVoucherTemplateRequest,
  IDuplicateVoucherTemplateResponse,
  IDeleteVoucherTemplateRequest,
  IDeleteVoucherTemplateResponse,
  IListBranchesRequest,
  IListBranchesResponse,
  // COA Mapping interfaces
  ISaveCOAMappingRequest,
  ISaveCOAMappingResponse,
  ILoadCOAMappingRequest,
  ILoadCOAMappingResponse,
  IBulkSaveCOAMappingRequest,
  IBulkSaveCOAMappingResponse,
  IToggleCOAMappingRequest,
  IToggleCOAMappingResponse,
} from "../modules/accounting/accounting.interface";

// ===== ACCOUNTING SERVICE =====

export const accountingService = {
  // ===== COA METHODS =====

  // 1. List COA with filtering and pagination
  async listCoa(data: IListCoaRequest): Promise<IListCoaResponse> {
    try {
      const response = await axiosInstance.post("/accounting/coa/list", data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to list COA");
    }
  },

  // 2. Create new COA entry
  async createCoa(data: ICreateCoaRequest): Promise<ICreateCoaResponse> {
    try {
      const response = await axiosInstance.post("/accounting/coa/create", data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to create COA");
    }
  },

  // 3. Update existing COA entry
  async updateCoa(data: IUpdateCoaRequest): Promise<IUpdateCoaResponse> {
    try {
      const response = await axiosInstance.post("/accounting/coa/update", data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to update COA");
    }
  },

  // 4. Delete COA entry (soft delete)
  async deleteCoa(data: IDeleteCoaRequest): Promise<IDeleteCoaResponse> {
    try {
      const response = await axiosInstance.post("/accounting/coa/delete", data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to delete COA");
    }
  },

  // 5. Split account into multiple accounts
  async splitCoa(data: ISplitCoaRequest): Promise<ISplitCoaResponse> {
    try {
      const response = await axiosInstance.post("/accounting/coa/split", data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to split COA");
    }
  },

  // 6. Merge accounts
  async mergeCoa(data: IMergeCoaRequest): Promise<IMergeCoaResponse> {
    try {
      const response = await axiosInstance.post("/accounting/coa/merge", data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to merge COA");
    }
  },

  // 7. Transfer balance between accounts
  async transferBalance(
    data: ITransferBalanceRequest
  ): Promise<ITransferBalanceResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/coa/transfer-balance",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to transfer balance"
      );
    }
  },

  // ===== JOURNAL METHODS =====

  // 8. List journal entries with filtering and pagination
  async listJournals(
    data: IListJournalEntriesRequest
  ): Promise<IListJournalEntriesResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/journals/list",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to list journal entries"
      );
    }
  },

  // 9. Create draft journal entry
  async createDraftJournal(
    data: IDraftJournalEntryRequest
  ): Promise<IDraftJournalEntryResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/journals/draft",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to create draft journal entry"
      );
    }
  },

  // 10. Create and post journal entry
  async postJournal(
    data: IPostJournalEntryRequest
  ): Promise<IPostJournalEntryResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/journals/post",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to post journal entry"
      );
    }
  },

  // 11. Get complete journal entry details
  async getJournal(
    data: IGetJournalEntryRequest
  ): Promise<IGetJournalEntryResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/journals/get",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to get journal entry"
      );
    }
  },

  // 12. Delete draft journal entry
  async deleteJournal(
    data: IDeleteJournalEntryRequest
  ): Promise<IDeleteJournalEntryResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/journals/delete",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to delete journal entry"
      );
    }
  },

  // 13. Post draft journal entry to ledger
  async postDraftJournal(
    data: IPostDraftJournalEntryRequest
  ): Promise<IPostDraftJournalEntryResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/journals/post-draft",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to post draft journal entry"
      );
    }
  },

  // 14. Reverse posted journal entry
  async reverseJournal(
    data: IReverseJournalEntryRequest
  ): Promise<IReverseJournalEntryResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/journals/reverse",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to reverse journal entry"
      );
    }
  },

  // ===== LEDGER METHODS =====

  // 15. Get ledger summary for specific account
  async getLedgerSummary(
    data: ILedgerSummaryRequest
  ): Promise<ILedgerSummaryResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/ledger/summary",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to get ledger summary"
      );
    }
  },

  // 16. Get paginated ledger entries for specific account
  async getLedgerEntries(
    data: ILedgerEntriesRequest
  ): Promise<ILedgerEntriesResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/ledger/entries",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to get ledger entries"
      );
    }
  },

  // 17. Get journal details from ledger view
  async getJournalDetails(
    data: IJournalDetailsRequest
  ): Promise<IJournalDetailsResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/ledger/journal-details",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to get journal details"
      );
    }
  },

  // ===== VOUCHER METHODS =====

  // 18. List vouchers with filtering and pagination
  async listVouchers(
    data: IListVouchersRequest
  ): Promise<IListVouchersResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/vouchers/list",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to list vouchers"
      );
    }
  },

  // 19. Get voucher templates filtered by type
  async getVoucherTemplates(
    data: IGetVoucherTemplatesRequest
  ): Promise<IGetVoucherTemplatesResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/vouchers/templates",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to get voucher templates"
      );
    }
  },

  // 20. Create draft voucher
  async createDraftVoucher(
    data: ICreateDraftVoucherRequest
  ): Promise<ICreateDraftVoucherResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/vouchers/draft",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to create draft voucher"
      );
    }
  },

  // 21. Post voucher (convert draft to posted)
  async postVoucher(data: IPostVoucherRequest): Promise<IPostVoucherResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/vouchers/post",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to post voucher"
      );
    }
  },

  // 22. Reverse posted voucher
  async reverseVoucher(
    data: IReverseVoucherRequest
  ): Promise<IReverseVoucherResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/vouchers/reverse",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to reverse voucher"
      );
    }
  },

  // 23. Get complete voucher details
  async getVoucher(data: IGetVoucherRequest): Promise<IGetVoucherResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/vouchers/get",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Failed to get voucher");
    }
  },

  // 24. Update draft voucher
  async updateDraftVoucher(
    data: IUpdateDraftVoucherRequest
  ): Promise<IUpdateDraftVoucherResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/vouchers/update",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to update draft voucher"
      );
    }
  },

  // 25. Delete draft voucher (soft delete)
  async deleteVoucher(
    data: IDeleteVoucherRequest
  ): Promise<IDeleteVoucherResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/vouchers/delete",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to delete voucher"
      );
    }
  },

  // ===== VOUCHER TEMPLATE METHODS =====

  // 26. List voucher templates with filtering and pagination
  async listVoucherTemplates(
    data: IListVoucherTemplatesRequest
  ): Promise<IListVoucherTemplatesResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/voucher-templates/list",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to list voucher templates"
      );
    }
  },

  // 27. Get complete voucher template details
  async getVoucherTemplate(
    data: IGetVoucherTemplateRequest
  ): Promise<IGetVoucherTemplateResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/voucher-templates/get",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to get voucher template"
      );
    }
  },

  // 28. Create new voucher template
  async createVoucherTemplate(
    data: ICreateVoucherTemplateRequest
  ): Promise<ICreateVoucherTemplateResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/voucher-templates/create",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to create voucher template"
      );
    }
  },

  // 29. Update existing voucher template
  async updateVoucherTemplate(
    data: IUpdateVoucherTemplateRequest
  ): Promise<IUpdateVoucherTemplateResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/voucher-templates/update",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to update voucher template"
      );
    }
  },

  // 30. Toggle voucher template status (active/inactive)
  async toggleTemplateStatus(
    data: IToggleTemplateStatusRequest
  ): Promise<IToggleTemplateStatusResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/voucher-templates/toggle-status",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to toggle template status"
      );
    }
  },

  // 31. Duplicate voucher template
  async duplicateVoucherTemplate(
    data: IDuplicateVoucherTemplateRequest
  ): Promise<IDuplicateVoucherTemplateResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/voucher-templates/duplicate",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to duplicate voucher template"
      );
    }
  },

  // 32. Delete voucher template (soft delete)
  async deleteVoucherTemplate(
    data: IDeleteVoucherTemplateRequest
  ): Promise<IDeleteVoucherTemplateResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/voucher-templates/delete",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to delete voucher template"
      );
    }
  },

  // ===== BRANCH METHODS =====

  // 33. List branches with filtering and pagination
  async listBranches(
    data: IListBranchesRequest
  ): Promise<IListBranchesResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/general/list-branches",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to list branches"
      );
    }
  },

  // ===== COA MAPPING CONFIGURATION METHODS =====

  // 34. Save COA mapping configuration
  async saveCOAMapping(
    data: ISaveCOAMappingRequest
  ): Promise<ISaveCOAMappingResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/coa-mappings/save",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          "Failed to save COA mapping configuration"
      );
    }
  },

  // 35. Load COA mapping configurations
  async loadCOAMappings(
    data: ILoadCOAMappingRequest
  ): Promise<ILoadCOAMappingResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/coa-mappings/load",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          "Failed to load COA mapping configurations"
      );
    }
  },

  // 36. Bulk save COA mapping configurations
  async bulkSaveCOAMappings(
    data: IBulkSaveCOAMappingRequest
  ): Promise<IBulkSaveCOAMappingResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/coa-mappings/bulk-save",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          "Failed to bulk save COA mapping configurations"
      );
    }
  },

  // 37. Toggle COA mapping status
  async toggleCOAMappingStatus(
    data: IToggleCOAMappingRequest
  ): Promise<IToggleCOAMappingResponse> {
    try {
      const response = await axiosInstance.post(
        "/accounting/coa-mappings/toggle-status",
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || "Failed to toggle COA mapping status"
      );
    }
  },
};
